import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Upload, Brain, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";

interface ParsedFlight {
  flight_number: string;
  departure_city_code: string;
  arrival_city_code: string;
  scheduled_departure: string;
  scheduled_arrival: string;
  aircraft_type: string;
  status: string;
}

interface ParseResult {
  organization_name: string;
  flights: ParsedFlight[];
  validation_errors: string[];
}

const AIAssistantPage = () => {
  const [file, setFile] = useState<File | null>(null);
  const [parsing, setParsing] = useState(false);
  const [importing, setImporting] = useState(false);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const { toast } = useToast();
  const { profile } = useAuth();

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type === 'application/pdf') {
        setFile(selectedFile);
        setParseResult(null);
        setImportResult(null);
      } else {
        toast({
          variant: "destructive",
          title: "Invalid file type",
          description: "Please select a PDF file.",
        });
      }
    }
  };

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result as string;
        // Remove the data URL prefix (e.g., "data:application/pdf;base64,")
        const base64Data = base64String.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = (error) => reject(error);
    });
  };

  const handleParseWithClaude = async () => {
    if (!file || !profile?.organization_id) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Missing file or organization information.",
      });
      return;
    }

    setParsing(true);
    try {
      console.log('Converting PDF to base64...');
      const base64File = await convertToBase64(file);
      
      console.log('Calling Claude AI edge function...');
      const { data: authData } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-flight-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authData.session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            file: base64File,
            fileName: file.name,
            organizationId: profile.organization_id,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to parse PDF');
      }

      const result = await response.json();
      console.log('Parse result:', result);
      
      // Validate response structure
      if (!result || !result.parsed_data) {
        throw new Error('Invalid response structure from AI parsing service');
      }
      
      const parsedData = result.parsed_data;
      const flights = parsedData.flights || [];
      
      setParseResult(parsedData);
      
      toast({
        title: "PDF Parsed Successfully",
        description: `Claude AI extracted ${flights.length} flights from the PDF.`,
      });
    } catch (error) {
      console.error('Error parsing PDF:', error);
      toast({
        variant: "destructive",
        title: "Parsing Error",
        description: error instanceof Error ? error.message : "Failed to parse PDF with Claude AI.",
      });
    } finally {
      setParsing(false);
    }
  };

  const handleImportFlights = async () => {
    if (!parseResult || !profile?.organization_id) return;

    setImporting(true);
    try {
      const { data: authData } = await supabase.auth.getSession();
      
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/parse-flight-pdf`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authData.session?.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            file: await convertToBase64(file!),
            fileName: file!.name,
            organizationId: profile.organization_id,
          }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to import flights');
      }

      const result = await response.json();
      
      // Validate response structure
      if (!result || !result.insert_result) {
        throw new Error('Invalid response structure from import service');
      }
      
      const insertResult = result.insert_result;
      setImportResult(insertResult);
      
      toast({
        title: "Flights Imported",
        description: `Successfully imported ${insertResult.inserted_count || 0} flights.`,
      });
    } catch (error) {
      console.error('Error importing flights:', error);
      toast({
        variant: "destructive",
        title: "Import Error",
        description: error instanceof Error ? error.message : "Failed to import flights.",
      });
    } finally {
      setImporting(false);
    }
  };

  const resetUpload = () => {
    setFile(null);
    setParseResult(null);
    setImportResult(null);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Brain className="h-8 w-8 text-primary" />
          AI Assistant - Claude Powered
        </h1>
        <p className="text-muted-foreground mt-2">
          Upload PDF flight schedules and let Claude AI extract and import flight data automatically
        </p>
      </div>

      <div className="grid gap-6">
        {/* Upload Section */}
        <Card>
          <CardHeader style={{ backgroundColor: 'orange' }}>
            <CardTitle>Upload PDF Schedule</CardTitle>
            <CardDescription>
              Select a PDF file containing your flight schedule. Claude AI will extract and structure the data.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              <Input
                type="file"
                accept="application/pdf"
                onChange={handleFileUpload}
                disabled={parsing}
                className="flex-1"
              />
              {file && (
                <Badge variant="outline" className="flex items-center gap-2">
                  <Upload className="h-4 w-4" />
                  {file.name}
                </Badge>
              )}
            </div>

            <div className="flex gap-2">
              <Button
                onClick={handleParseWithClaude}
                disabled={!file || parsing}
                className="flex items-center gap-2"
              >
                {parsing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyzing with Claude AI...
                  </>
                ) : (
                  <>
                    <Brain className="h-4 w-4" />
                    Submit with NAC AI Assistant
                  </>
                )}
              </Button>
              {(parseResult || importResult) && (
                <Button onClick={resetUpload} variant="outline">
                  Upload New File
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results Section */}
        {parseResult && (
          <Card>
            <CardHeader style={{ backgroundColor: 'orange' }}>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Parsed Results from Claude AI
              </CardTitle>
              <CardDescription>
                Organization: {parseResult.organization_name} | {parseResult.flights.length} flights extracted
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Validation Errors */}
              {parseResult.validation_errors.length > 0 && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    <div className="font-semibold mb-2">Validation Errors:</div>
                    <ul className="list-disc list-inside space-y-1">
                      {parseResult.validation_errors.map((error, idx) => (
                        <li key={idx}>{error}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              )}

              {/* Flights Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Flight</TableHead>
                      <TableHead>Route</TableHead>
                      <TableHead>Departure</TableHead>
                      <TableHead>Arrival</TableHead>
                      <TableHead>Aircraft</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parseResult.flights.map((flight, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{flight.flight_number}</TableCell>
                        <TableCell>
                          {flight.departure_city_code} → {flight.arrival_city_code}
                        </TableCell>
                        <TableCell>{new Date(flight.scheduled_departure).toLocaleString()}</TableCell>
                        <TableCell>{new Date(flight.scheduled_arrival).toLocaleString()}</TableCell>
                        <TableCell>{flight.aircraft_type}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{flight.status}</Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Import Button */}
              {parseResult.flights.length > 0 && !importResult && (
                <Button
                  onClick={handleImportFlights}
                  disabled={importing}
                  className="w-full"
                >
                  {importing ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Importing Flights...
                    </>
                  ) : (
                    'Import All Flights to Database'
                  )}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        {/* Import Results */}
        {importResult && (
          <Card>
            <CardHeader style={{ backgroundColor: 'orange' }}>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Import Complete
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Alert>
                <AlertDescription>
                  <div className="space-y-2">
                    <div className="font-semibold">
                      Successfully imported {importResult.inserted_count} flights
                    </div>
                    {importResult.failed_count > 0 && (
                      <div className="text-red-600">
                        Failed to import {importResult.failed_count} flights
                      </div>
                    )}
                  </div>
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AIAssistantPage;
